import Phaser from "phaser";
import type { EntityState } from "odyssey-shared";
import type { HitFeed, HitReading } from "../../fight/hit-feed.ts";
import { PLATE_LIFT } from "../health-bars/bar.ts";
import type { WorldUiModule } from "../hook.ts";
import { ABOVE_HEAD, LINE } from "../nameplates/layout.ts";
import { PIXEL_FONT_KEY, pixelFontSize } from "../pixel-font.ts";
import { negationWord, numberRise, numberText, numberTint, spreadOf } from "./motion.ts";
import type { DamageNumberReport, NumberReading } from "./report.ts";

/**
 * Damage numbers on the canvas (P2.1-T4; question 5: numbers only for the hits the player deals and takes; ADR-061
 * decisions 1 to 3 and 7): each hit the page's combat frames name for the player (`fight/hit-feed.ts`) becomes one
 * `BitmapText` in the pixel face, standing over the character it landed on, above where the plate and its bar
 * stand, and rising for its life (`motion.ts`). It keeps the world point it appeared at, so it rises where the blow
 * landed as the character moves on. A number over the player's own character for damage it took is red and signed
 * "-", a heal green and signed "+", and one over another its damage type's colour (ADR-079's amendment for
 * P1.9-T66). A negated hit shows its cause's word at the plain size and life in the muted colour (ADR-079 decision
 * 6). The UI camera draws it at the UI scale.
 *
 * A hit the player dealt to a training dummy (ADR-058's amendment for P2.2-T7) stands over the top of the dummy's
 * art instead, read from the scene's things.
 *
 * A number over an entity the viewer cannot see (ADR-061's amendment for P1.9-T73: concealed behind a prop or under a
 * roof the viewer is not under, and not the viewer's target) is hidden, whether it is starting or already rising, and
 * keeps its time, so it shows again for what is left of its life the frame the entity does. While a number shows or a
 * hit waits, the frame visits the characters drawn once to place the waiting hits and to read each number's target.
 *
 * Numbers come from a pool of at most `MAX_NUMBERS`, made as the most at once so far need them; with every one
 * showing, the oldest gives way. A hit whose character or thing is not drawn this frame draws nothing and is counted. A frame
 * with no hit and no number showing touches nothing; a showing number is placed only when its device position moves.
 * Only a hit allocates, for the text it sets.
 */

/**
 * The most numbers showing at once.
 */
export const MAX_NUMBERS = 32;

/**
 * How far over the head a number starts, in logical pixels: over a plate whose bar shows, and a pixel more.
 */
const NUMBER_BASE = ABOVE_HEAD + PLATE_LIFT + LINE.height + 1;

/**
 * How far over the top of a thing's art a number starts, in logical pixels: a thing carries no plate or bar, so a
 * number dealt to a training dummy starts just above its head.
 */
const THING_NUMBER_BASE = ABOVE_HEAD;

/**
 * A hit copied out of the feed for the frame, and whether its character was found.
 */
interface WaitingHit extends HitReading {
  placed: boolean;
}

/**
 * One number and what it shows.
 */
interface FloatingNumber {
  readonly text: Phaser.GameObjects.BitmapText;
  target: number;
  worldX: number;
  worldY: number;
  startMs: number;
  critical: boolean;
  taken: boolean;
  spread: number;
  lift: number;
  left: number;
  top: number;
  active: boolean;
  hidden: boolean;
}

/**
 * The damage numbers surface.
 *
 * @param report The game report's `damageNumbers` entry.
 * @param hits The feed the session pushes the player's hits into.
 * @returns The surface's module, listed after the health bars.
 */
export function damageNumbers(report: DamageNumberReport, hits: HitFeed): WorldUiModule {
  return {
    id: "damage-numbers",
    /**
     * Builds the pool, the waiting hits' records and the frame's visitors.
     *
     * @param context The UI layer, the projection and the characters the scene draws.
     * @returns The surface.
     */
    create(context) {
      const scene = context.scene;
      const numbers: FloatingNumber[] = [];
      const waiting: WaitingHit[] = [];
      let waitingCount = 0;
      let showing = 0;
      let now = 0;
      const drawn = { x: 0, y: 0 };
      const anchor = { x: 0, y: 0 };

      /**
       * A number to show: a free one, else a new one while fewer than `MAX_NUMBERS` exist, else the oldest.
       *
       * @returns The number.
       */
      const take = (): FloatingNumber => {
        let oldest: FloatingNumber | null = null;
        for (let index = 0; index < numbers.length; index += 1) {
          const number = numbers[index] as FloatingNumber;
          if (!number.active) {
            return number;
          }
          if (oldest === null || number.startMs < oldest.startMs) {
            oldest = number;
          }
        }
        if (numbers.length < MAX_NUMBERS || oldest === null) {
          const text = context.add(new Phaser.GameObjects.BitmapText(scene, 0, 0, PIXEL_FONT_KEY, "", pixelFontSize("number")));
          text.setOrigin(0, 0).setVisible(false);
          const made: FloatingNumber = { text, target: -1, worldX: 0, worldY: 0, startMs: 0, critical: false, taken: false, spread: 0, lift: NUMBER_BASE, left: Number.NaN, top: Number.NaN, active: false, hidden: false };
          numbers.push(made);
          report.created = numbers.length;
          return made;
        }
        return oldest;
      };

      /**
       * Copies a hit from the feed into a waiting record, since the feed reuses its own.
       *
       * @param hit The hit.
       */
      const hold = (hit: Readonly<HitReading>): void => {
        let slot = waiting[waitingCount];
        if (slot === undefined) {
          slot = { target: 0, amount: 0, critical: false, taken: false, damageType: 0, cause: 0, heal: false, placed: false };
          waiting.push(slot);
        }
        slot.target = hit.target;
        slot.amount = hit.amount;
        slot.critical = hit.critical;
        slot.taken = hit.taken;
        slot.damageType = hit.damageType;
        slot.cause = hit.cause;
        slot.heal = hit.heal;
        slot.placed = false;
        waitingCount += 1;
      };

      /**
       * Starts one waiting hit's number at a world point.
       *
       * @param hit The hit.
       * @param handle The handle it landed on.
       * @param x Where it starts across, in world pixels.
       * @param y Where it starts down, in world pixels: the head or the top of the art.
       * @param lift How far above that point it starts, in logical pixels.
       * @param hidden Whether the entity it landed on hides its world UI.
       */
      const start = (hit: WaitingHit, handle: number, x: number, y: number, lift: number, hidden: boolean): void => {
        hit.placed = true;
        let order = 0;
        for (let other = 0; other < numbers.length; other += 1) {
          if ((numbers[other] as FloatingNumber).active && (numbers[other] as FloatingNumber).target === handle) {
            order += 1;
          }
        }
        const number = take();
        const critical = hit.critical && negationWord(hit.cause) === null;
        number.text.setFontSize(pixelFontSize(critical ? "number-lg" : "number"));
        number.text.setText(numberText(hit));
        number.text.setTint(numberTint(hit));
        number.target = handle;
        number.worldX = x;
        number.worldY = y;
        number.startMs = now;
        number.critical = critical;
        number.taken = hit.taken;
        number.spread = spreadOf(order);
        number.lift = lift;
        number.left = Number.NaN;
        number.active = true;
        number.hidden = hidden;
        report.spawned += 1;
        if (hit.taken) {
          report.taken += 1;
        } else {
          report.dealt += 1;
        }
      };

      /**
       * Starts the number of each waiting hit on a drawn character, over its head, and marks every number over it shown
       * or hidden as its world UI is.
       *
       * @param handle The character's handle.
       * @param x Its feet across, in world pixels.
       * @param y Its feet down, in world pixels.
       * @param _state Its state, unread.
       * @param head How far its art reaches above its feet, in world pixels, a creature's its own.
       * @param hidden Whether its world UI hides (ADR-061's amendment for P1.9-T73).
       */
      const place = (handle: number, x: number, y: number, _state: EntityState, head: number, hidden: boolean): void => {
        for (let index = 0; index < waitingCount; index += 1) {
          const hit = waiting[index] as WaitingHit;
          if (!hit.placed && hit.target === handle) {
            start(hit, handle, x, y - head, NUMBER_BASE, hidden);
          }
        }
        for (let index = 0; index < numbers.length; index += 1) {
          const number = numbers[index] as FloatingNumber;
          if (number.active && number.target === handle) {
            number.hidden = hidden;
          }
        }
      };

      /**
       * Moves one showing number up for the frame, or ends it.
       *
       * @param number The number.
       */
      const rise = (number: FloatingNumber): void => {
        const risen = numberRise(now - number.startMs, number.critical);
        if (risen === null) {
          number.active = false;
          number.text.setVisible(false);
          return;
        }
        if (number.hidden) {
          if (number.text.visible) {
            number.text.setVisible(false);
          }
          return;
        }
        const scale = context.scale.current;
        context.project(number.worldX, number.worldY, anchor);
        const left = Math.round(anchor.x * scale) - Math.floor((number.text.width * scale) / 2) + number.spread * scale;
        const top = Math.round(anchor.y * scale) - (number.lift + risen + number.text.height) * scale;
        if (left !== number.left || top !== number.top) {
          number.text.setPosition(left / scale, top / scale);
          number.left = left;
          number.top = top;
        }
        if (!number.text.visible) {
          number.text.setVisible(true);
        }
      };

      /**
       * Reads every number showing, for the report: made on request, never on a frame.
       *
       * @returns One reading a number.
       */
      report.list = (): readonly NumberReading[] => {
        const readings: NumberReading[] = [];
        for (const number of numbers) {
          if (number.active) {
            readings.push({ target: number.target, text: number.text.text, taken: number.taken, tint: number.text.tintTopLeft, x: number.text.x, y: number.text.y, inUiLayer: number.text.displayList === context.layer, hidden: number.hidden });
          }
        }
        return readings;
      };

      return {
        /**
         * Starts the numbers of the hits that arrived since the last frame, then moves every number showing.
         *
         * @param time Scene time in ms.
         */
        update(time) {
          now = time;
          waitingCount = 0;
          hits.drain(hold);
          if ((waitingCount > 0 || showing > 0) && context.characters.local(drawn)) {
            context.characters.forEach(place);
          }
          if (waitingCount > 0) {
            for (let index = 0; index < waitingCount; index += 1) {
              const hit = waiting[index] as WaitingHit;
              if (!hit.placed && context.things.top(hit.target, drawn)) {
                start(hit, hit.target, drawn.x, drawn.y, THING_NUMBER_BASE, false);
              }
              if (!hit.placed) {
                report.unplaced += 1;
              }
            }
          }
          let active = 0;
          for (let index = 0; index < numbers.length; index += 1) {
            const number = numbers[index] as FloatingNumber;
            if (number.active) {
              rise(number);
              if (number.active) {
                active += 1;
              }
            }
          }
          showing = active;
          report.active = active;
        },
        /**
         * Forgets the report's list, which would hold the destroyed numbers.
         */
        destroy() {
          report.list = () => [];
        },
      };
    },
  };
}
